import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import Checkmark from './components/Checkmark';

export default function SleepLevel() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [levelIndex, setLevelIndex] = useState<number>(2);

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const sleepLevels: { en: string; es: string; level: number }[] = useMemo(
    () =>
      englishChoices?.length
        ? englishChoices.map((choice: string, index: number) => ({
            en: choice,
            es: spanishChoices[index],
            level: index + 1,
          }))
        : [],
    [englishChoices, spanishChoices]
  );

  const levelsWithLableComment = sleepLevels?.length
    ? sleepLevels.map((level) => ({
        ...level,
        en: extractLabelAndComment(level.en),
        es: extractLabelAndComment(level.es),
      }))
    : [];

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const labels = levelsWithLableComment.map(
        (langLevel) => langLevel?.[isSpanish ? 'es' : 'en']?.label
      );
      const choice =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];

      if (typeof choice === 'number') {
        setLevelIndex(choice > 0 ? choice - 1 : 2);
        return;
      }

      const answer = choice?.split('<comment>')[0];
      const index = labels.indexOf(answer.trim());
      setLevelIndex(index > 0 ? index : 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  const onContinue = () => {
    const sleepItem = sleepLevels[levelIndex];
    onSubmit({
      choice_value: sleepItem.en,
      spanish_choice_value: sleepItem.es,
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={3}
    >
      <div className='grid grid-cols-1 gap-4 lg:gap-6 lg:grid-cols-2'>
        {sleepLevels?.length
          ? levelsWithLableComment.map((level, index) => {
              const isSelected = index === levelIndex;

              return (
                <motion.div
                  key={level.level}
                  onClick={() => setLevelIndex(index)}
                  className={cn(
                    'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76]',
                    isSelected && 'border-primary text-primary bg-primary/10'
                  )}
                  whileTap={{ scale: 0.98 }}
                >
                  <p className='text-sm font-medium leading-5'>
                    <span className='font-semibold'>
                      {level?.[isSpanish ? 'es' : 'en']?.label}
                    </span>{' '}
                    [{level?.[isSpanish ? 'es' : 'en']?.comment}]
                  </p>
                  <Checkmark isSelected={isSelected} />
                </motion.div>
              );
            })
          : []}
      </div>
    </QuestionnaireContainer>
  );
}
